// shiftsApi — typed CRUD for /v1/schedules/shifts (Route 10).
import { apiFetch } from "../../lib/api-client.js";

export type ShiftStatus = "active" | "inactive";

export interface ShiftRow {
  id: string;
  tenant_id: string;
  name: string;
  start_time: string; // HH:MM
  end_time: string;
  days_mask: number;  // bit 0=Mon ... bit 6=Sun
  officer_count: number;
  status: ShiftStatus;
  created_at: string;
  updated_at: string;
}

export interface ShiftListResponse { items: ShiftRow[]; correlation_id: string | null }

export class ShiftsApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[]; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields; this.upstreamMessage = upstreamMessage;
  }
}
export function friendlyShiftsError(err: unknown): string {
  if (err instanceof ShiftsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改班別。";
    if (err.status === 404) return "找不到此班別。";
    if (err.status === 400 && err.code === "tenant_id_required") return "尚未選定操作的單位。";
    if (err.status === 400 && err.code === "invalid_body") return `欄位填寫不完整：${(err.fields ?? []).join("、")}。`;
    if (err.status === 400) return "資料格式不正確。";
    const msgPart = err.upstreamMessage ? `・msg=${err.upstreamMessage}` : "";
    return `班別資料暫時無法存取（HTTP ${err.status}・code=${err.code}${msgPart}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<ShiftsApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined; let msg: string | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; msg = b.message; } catch {}
  return new ShiftsApiError(r.status, code, cid, fields, msg);
}

export async function listShifts(tenantId?: string | null): Promise<ShiftListResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/shifts${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ShiftListResponse;
}

export interface CreateShiftBody {
  name: string; start_time: string; end_time: string;
  days_mask: number; officer_count: number; status?: ShiftStatus;
}
export async function createShift(b: CreateShiftBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/shifts${qs}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(b), credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}
export type PatchShiftBody = Partial<CreateShiftBody>;
export async function patchShift(id: string, b: PatchShiftBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/shifts/${encodeURIComponent(id)}${qs}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(b), credentials: "include" });
  if (!r.ok) throw await asError(r);
}
export async function archiveShift(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/shifts/${encodeURIComponent(id)}${qs}`, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw await asError(r);
}
